import { useRef, useCallback } from 'react'
import messageService from '../utils/message-service'

/**
 * Custom hook for managing dynamic session timeout changes
 * This hook allows the timeout to be changed dynamically (e.g., when a payment method is selected)
 * and sends postMessage notifications to the merchant
 *
 * @param {Object} options - Configuration options
 * @param {number} options.initialTimeout - Initial timeout duration in milliseconds
 * @param {Function} options.onTimeoutChange - Callback when timeout changes
 * @returns {Object} - Timeout control functions
 */
const useDynamicSessionTimeout = ({ initialTimeout = 60 * 60 * 1000, onTimeoutChange } = {}) => {
    const currentTimeoutRef = useRef(initialTimeout)
    const timeoutHistoryRef = useRef([
        {
            timeout: initialTimeout,
            timestamp: Date.now(),
            reason: 'Initial timeout',
        },
    ])

    /**
     * Change the session timeout duration
     * @param {number} newTimeout - New timeout duration in milliseconds
     * @param {string} reason - Reason for the timeout change
     */
    const changeTimeout = useCallback(
        (newTimeout, reason = 'Timeout changed') => {
            // if (!enabled) {
            //     console.warn('[useDynamicSessionTimeout] Dynamic timeout is disabled')
            //     return
            // }

            const previousTimeout = currentTimeoutRef.current

            // Validate the new timeout
            if (typeof newTimeout !== 'number' || newTimeout <= 0) {
                console.error('[useDynamicSessionTimeout] Invalid timeout value:', newTimeout)
                return
            }

            // Update the current timeout
            currentTimeoutRef.current = newTimeout

            // Add to history
            timeoutHistoryRef.current.push({
                timeout: newTimeout,
                previousTimeout,
                timestamp: Date.now(),
                reason,
            })

            // Log the change
            // eslint-disable-next-line no-console
            console.log('[useDynamicSessionTimeout] Timeout changed:', {
                from: previousTimeout,
                to: newTimeout,
                reason,
            })

            // Send postMessage to merchant
            messageService.sendTimeoutChanged(newTimeout, reason, {
                previousTimeout,
                timestamp: Date.now(),
            })

            // Call the callback if provided
            if (onTimeoutChange) {
                onTimeoutChange({
                    newTimeout,
                    previousTimeout,
                    reason,
                })
            }
        },
        [onTimeoutChange],
    )

    /**
     * Get the current timeout value
     */
    const getCurrentTimeout = useCallback(() => {
        return currentTimeoutRef.current
    }, [])

    /**
     * Get the timeout history
     */
    const getTimeoutHistory = useCallback(() => {
        return [...timeoutHistoryRef.current]
    }, [])

    /**
     * Reset timeout to initial value
     */
    const resetTimeout = useCallback(() => {
        changeTimeout(initialTimeout, 'Reset to initial timeout')
    }, [initialTimeout, changeTimeout])

    return {
        changeTimeout,
        getCurrentTimeout,
        getTimeoutHistory,
        resetTimeout,
        currentTimeout: currentTimeoutRef.current,
    }
}

export default useDynamicSessionTimeout
